import React from 'react';

interface ClockDisplayProps {
    /** 开始时间 - 小时 (0-23) */
    startHour: number;
    /** 开始时间 - 分钟 (0-59) */
    startMinute: number;
    /** 结束时间 - 小时 (0-23) */
    endHour: number;
    /** 结束时间 - 分钟 (0-59) */
    endMinute: number;
    /** 时钟大小 */
    size?: number;
    /** 高亮颜色 */
    highlightColor?: string;
}

export const ClockDisplay: React.FC<ClockDisplayProps> = ({
    startHour,
    startMinute,
    endHour,
    endMinute,
    size = 80,
    highlightColor = '#3b82f6'
}) => {
    const radius = size / 2;
    const center = radius;

    // 将时间转换为角度 (从12点开始，顺时针)
    const timeToAngle = (hour: number, minute: number) => {
        const totalMinutes = (hour % 12) * 60 + minute;
        return (totalMinutes / 720) * 360 - 90; // -90度让12点在顶部
    };

    const startAngle = timeToAngle(startHour, startMinute);
    const endAngle = timeToAngle(endHour, endMinute);

    // 计算高亮弧线的路径
    const createArcPath = () => {
        const arcRadius = radius - 8;
        const startX = center + arcRadius * Math.cos((startAngle * Math.PI) / 180);
        const startY = center + arcRadius * Math.sin((startAngle * Math.PI) / 180);
        const endX = center + arcRadius * Math.cos((endAngle * Math.PI) / 180);
        const endY = center + arcRadius * Math.sin((endAngle * Math.PI) / 180);

        // 判断是否为大弧线
        const normalizeAngle = (angle: number) => {
            while (angle < 0) angle += 360;
            while (angle >= 360) angle -= 360;
            return angle;
        };

        const normalizedStart = normalizeAngle(startAngle);
        const normalizedEnd = normalizeAngle(endAngle);
        let sweepAngle = normalizedEnd - normalizedStart;
        if (sweepAngle < 0) sweepAngle += 360;
        const largeArcFlag = sweepAngle > 180 ? 1 : 0;

        return `M ${startX} ${startY} A ${arcRadius} ${arcRadius} 0 ${largeArcFlag} 1 ${endX} ${endY}`;
    };

    // 生成主要时间点的标记点和数字（12, 3, 6, 9点）
    const generateDotsAndNumbers = () => {
        const elements: React.ReactElement[] = [];
        const mainHours = [
            { hour: 0, display: '12' },
            { hour: 3, display: '3' },
            { hour: 6, display: '6' },
            { hour: 9, display: '9' }
        ];

        mainHours.forEach(({ hour, display }) => {
            const angle = (hour * 90) - 90;
            const dotX = center + (radius - 6) * Math.cos((angle * Math.PI) / 180);
            const dotY = center + (radius - 6) * Math.sin((angle * Math.PI) / 180);
            const textX = center + (radius - 16) * Math.cos((angle * Math.PI) / 180);
            const textY = center + (radius - 16) * Math.sin((angle * Math.PI) / 180);

            // 添加小圆点
            elements.push(
                <circle
                    key={`dot-${hour}`}
                    cx={dotX}
                    cy={dotY}
                    r="1.5"
                    fill="#e5e7eb"
                />
            );

            // 添加数字
            elements.push(
                <text
                    key={`number-${hour}`}
                    x={textX}
                    y={textY}
                    textAnchor="middle"
                    dominantBaseline="central"
                    className="text-xs font-medium"
                    fill="#9ca3af"
                >
                    {display}
                </text>
            );
        });

        return elements;
    };

    return (
        <div className="flex items-center justify-center">
            <svg width={size} height={size}>
                {/* 时钟外圈 */}
                <circle
                    cx={center}
                    cy={center}
                    r={radius - 2}
                    fill="white"
                    stroke="#f3f4f6"
                    strokeWidth="1"
                />

                {/* 主要时间点和数字 */}
                {generateDotsAndNumbers()}

                {/* 高亮时间段弧线 */}
                <path
                    d={createArcPath()}
                    fill="none"
                    stroke={highlightColor}
                    strokeWidth="4"
                    strokeLinecap="round"
                    opacity="0.8"
                />

                {/* 中心点 */}
                <circle
                    cx={center}
                    cy={center}
                    r="1.5"
                    fill="#d1d5db"
                />
            </svg>
        </div>
    );
};

export default ClockDisplay;
